import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Breadcrumbs, Button, Container, Grid, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Field } from 'src/API';
import { FIELD_TYPES, FieldInput, FormInput, processModuleApi } from 'src/api/process-module';
import { useForms } from 'src/hooks/use-forms';
import { useProcessModules } from 'src/hooks/use-process-modules';
import logger from 'src/log/log';
import { paths } from 'src/paths';
import { useDispatch, useSelector } from 'src/store';
import { TEST_IDS } from 'src/test-ids';
import { thunks } from 'src/thunks/process-module';
import { FormFieldPreview } from './FieldTypes/FormFieldPreview';
import { FieldEditor } from './FieldTypes/preview/FieldEditor';
import { useFieldAutoSave } from './hooks/useFieldAutoSave';

const DEFAULT_FIELD: FieldInput = {
  id: '', // Will be generated by backend
  type: FIELD_TYPES.DIVIDER,
  name: '',
  label: '',
  required: false,
  properties: undefined, // Explicitly set to undefined instead of omitting
};

const DEFAULT_FORM: FormInput = {
  name: '',
  description: '',
  fields: [] as (FieldInput & Partial<Field>)[],
};

const FormBuilder = () => {
  const { moduleId, formId } = useParams<{
    moduleId: string;
    formId: string;
  }>();
  const { forms, fields } = useForms(moduleId || '');
  const modules = useProcessModules();
  const currentModule = modules.find((m) => m.id === moduleId);
  // const userOrganizations = useUserOrganizations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingFieldIds, setSavingFieldIds] = useState<Set<string>>(new Set());
  const [organizationId, setOrganizationId] = useState<string>();
  const [formData, setFormData] = useState<FormInput>(DEFAULT_FORM);
  const [expandedFieldIndex, setExpandedFieldIndex] = useState<number | null>(null);
  const [originalFormName, setOriginalFormName] = useState<string>('');
  const [isSavingFormName, setIsSavingFormName] = useState(false);
  const isEditMode = useMemo(() => formId !== 'new', [formId]);
  const dispatch = useDispatch();
  const processModuleState = useSelector((state) => state.processModule);
  const form = forms.find((f) => f.id === formId);
  const navigate = useNavigate();
  const formFields = useMemo(() => {
    try {
      return JSON.parse(form?.fields ?? '[]') as FieldInput[];
    } catch (e) {
      console.error('Error parsing form fields:', e);
      return [];
    }
  }, [form?.fields]);

  const fieldMap = useMemo(() => {
    return new Map<string, Field>(fields.map((f) => [f.id, f]));
  }, [fields]);

  const [selectedFieldIds, setSelectedFieldIds] = useState<{
    [key: number]: string;
  }>({});

  // Auto-save hook for fields
  const { saveFieldImmediately, hasPendingSave, flushAllPendingSaves } = useFieldAutoSave({
    organizationId: organizationId || '',
    processModuleId: moduleId || '',
    onSaveSuccess: (savedField) => {
      logger.log('Field saved successfully:', savedField);
      // Refresh the form data to get the latest field state
      if (moduleId) {
        dispatch(thunks.getFormsForProcessModule(moduleId));
        dispatch(thunks.getFields());
      }
    },
    onSaveError: (error) => {
      logger.error('Field auto-save error:', error);
    },
  });

  useEffect(() => {
    if (form && formData.fields.length === 0) {
      logger.log('setting form data from form', form);
      const formName = form.name || '';
      setFormData({
        name: formName,
        description: '', // Description field removed from UI but keep for API compatibility
        fields: formFields,
      });
      setOriginalFormName(formName);
    }
  }, [form, formFields, formData.fields]);

  // Update form data from field map
  useEffect(() => {
    const fieldsNotFound: string[] = [];
    logger.log('setting form data from fieldMap', formFields, fieldMap);
    setFormData((prev) => ({
      ...prev,
      fields: formFields.map((f) => {
        const field = fieldMap.get(f.id);

        if (!field) {
          fieldsNotFound.push(f.id);
          return f;
        }
        return {
          ...field,
          properties: {
            ...((field as any).properties || {}),
            ...(f.properties || {}),
          },
          type: field.type,
          label: field.label || '',
          name: field.name || '',
          // Prioritize form config dataSource over field document dataSource
          dataSource: f.properties?.dataSource ?? field.dataSource,
          dataSourceType: f.properties?.dataSourceType ?? field.dataSourceType,
        };
      }),
    }));

    if (fieldsNotFound.length > 0) {
      logger.warn('Fields not found:', fieldsNotFound);
    }
  }, [formFields, fieldMap]);

  // useEffect(() => {
  //   if (
  //     !processModuleState._status.processModules?.hasFetched &&
  //     !processModuleState._status.processModules?.isFetching
  //   ) {
  //     logger.log("fetching process modules");

  //   }
  // }, [moduleId, dispatch, processModuleState._status.processModules]);

  // set the organization id
  useEffect(() => {
    if (processModuleState.processModules.list.length > 0) {
      const processModule = processModuleState.processModules.list.find((pm) => pm.id === moduleId);
      if (processModule) {
        logger.log('setting organization id', processModule.managerGroups?.[0]);
        setOrganizationId(processModule.managerGroups?.[0] ?? '');
      }
    }
  }, [processModuleState.processModules.list, moduleId]);

  const isFieldUnsaved = useCallback((field: FieldInput & Partial<Field>) => {
    return field.id.startsWith('temp-');
  }, []);

  const handleAddField = useCallback(() => {
    // Check if there's an unsaved field
    const unsavedFieldIndex = formData.fields.findIndex((field) => isFieldUnsaved(field));
    if (unsavedFieldIndex !== -1) {
      toast.error('Please save the current field before adding a new one');
      setExpandedFieldIndex(unsavedFieldIndex);
      return;
    }

    // Add a new field to local state with a temporary ID
    const newFieldIndex = formData.fields.length;
    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, { ...DEFAULT_FIELD, id: `temp-${crypto.randomUUID()}` }],
    }));
    // Automatically expand the new field
    setExpandedFieldIndex(newFieldIndex);
  }, [formData.fields, isFieldUnsaved]);

  const handleRemoveField = useCallback(
    async (index: number) => {
      const field = formData.fields[index];

      if (!moduleId || !organizationId || !formId || formId === 'new' || !field) {
        // If it's a new form, just remove from local state
        setFormData((prev) => ({
          ...prev,
          fields: prev.fields.filter((_, i) => i !== index),
        }));
        return;
      }

      // If field has temp ID, it hasn't been saved yet, just remove from local state
      if (field.id.startsWith('temp-')) {
        setFormData((prev) => ({
          ...prev,
          fields: prev.fields.filter((_, i) => i !== index),
        }));
        toast.success('Field removed');
        return;
      }

      try {
        setIsSubmitting(true);

        // Permanently delete the field (not just unlink from form)
        const result = await processModuleApi.deleteField(organizationId, field.id, 'permanent');

        if (!result.ok) {
          logger.error('Failed to unlink field from form', result);
          toast.error('Failed to remove field');
          return;
        }

        // Update local state to remove the field
        setFormData((prev) => ({
          ...prev,
          fields: prev.fields.filter((_, i) => i !== index),
        }));

        toast.success('Field deleted permanently');

        // Refresh forms and fields from backend to ensure consistency
        if (moduleId) {
          dispatch(thunks.getFormsForProcessModule(moduleId));
          dispatch(thunks.getFields());
        }
      } catch (error) {
        logger.error('Error removing field:', error);
        toast.error('Failed to remove field');
      } finally {
        setIsSubmitting(false);
      }
    },
    [moduleId, organizationId, formId, formData.fields, dispatch],
  );

  const handleFieldChange = (index: number, field: Partial<FieldInput>) => {
    // Update form data directly
    setFormData((prev) => {
      const updatedFields = prev.fields.map((f, i) => {
        if (i === index) {
          // Deep merge properties to avoid losing nested property updates
          const updatedField = {
            ...f,
            ...field,
            properties: field.properties
              ? {
                  ...f.properties,
                  ...field.properties,
                }
              : f.properties,
          };
          return updatedField;
        }
        return f;
      });
      return { ...prev, fields: updatedFields };
    });
  };

  const handleFieldSelection = useCallback((index: number, fieldId: string) => {
    setSelectedFieldIds((prev) => ({
      ...prev,
      [index]: fieldId,
    }));
  }, []);

  const handleAccordionChange = useCallback(
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      const field = formData.fields[index];

      // If trying to collapse an unsaved field, prevent it
      if (!isExpanded && field && isFieldUnsaved(field)) {
        toast.error('Please save this field before collapsing');
        event.preventDefault();
        return;
      }

      // If trying to expand while another unsaved field exists, prevent it
      if (isExpanded) {
        const unsavedFieldIndex = formData.fields.findIndex((f, i) => i !== index && isFieldUnsaved(f));
        if (unsavedFieldIndex !== -1) {
          toast.error('Please save the unsaved field before opening another');
          event.preventDefault();
          return;
        }
      }

      setExpandedFieldIndex(isExpanded ? index : null);
    },
    [formData.fields, isFieldUnsaved],
  );

  const isFieldUsingExisting = useCallback(
    (index: number) => {
      return !!selectedFieldIds[index];
    },
    [selectedFieldIds],
  );

  const handleSaveField = useCallback(
    async (index: number) => {
      const field = formData.fields[index];
      if (!field || !moduleId || !organizationId || !formId || formId === 'new') {
        return;
      }

      // Validation for root canvas fields
      if (field.type === FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS) {
        const canvasId = field.properties?.canvasId;
        const canvasCreated = field.properties?.canvasCreated;
        const isRootField = field.properties?.isRootField;
        const referencedFieldId = field.properties?.referencedFieldId;

        logger.log('Root canvas field save validation:', {
          canvasId,
          canvasCreated,
          isRootField,
          referencedFieldId,
          hasCanvasId: !!canvasId,
          hasCanvasCreated: !!canvasCreated,
          properties: field.properties,
        });

        // Check if canvas is configured
        if (!canvasId || !canvasCreated) {
          toast.error('Please configure the canvas before saving (select template and click "Configure Canvas")');
          setSavingFieldIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(field.id);
            return newSet;
          });
          return;
        }

        // Check if it's a root field or a reference
        if (!isRootField && !referencedFieldId) {
          toast.error('Canvas field must either create a new canvas or reference an existing canvas field');
          setSavingFieldIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(field.id);
            return newSet;
          });
          return;
        }
      }

      // Mark this field as saving
      setSavingFieldIds((prev) => new Set(prev).add(field.id));

      try {
        // Check if this is a new field (has temp ID)
        if (field.id.startsWith('temp-')) {
          // Create field via API
          const response = await processModuleApi.createField(organizationId, moduleId, {
            type: field.type,
            label: field.label || '',
            name: field.name,
            required: field.required,
            properties: field.properties,
            dataSource: field.dataSource,
            dataSourceType: field.dataSourceType,
            formId: formId,
          } as Partial<FieldInput & Partial<Field> & { formId: string }>);

          if (!response || !response.ok) {
            toast.error('Failed to create field');
            return;
          }

          const newField = response.result.field;

          // Update the field in local state with the real ID
          setFormData((prev) => ({
            ...prev,
            fields: prev.fields.map((f, i) => {
              if (i === index) {
                return {
                  ...f,
                  id: newField.id,
                  // Update with any other data returned from backend
                };
              }
              return f;
            }),
          }));

          toast.success('Field created successfully');

          // Refresh forms and fields from backend
          if (moduleId) {
            dispatch(thunks.getFormsForProcessModule(moduleId));
            dispatch(thunks.getFields());
          }

          // Clear the expanded state since field is now saved
          setExpandedFieldIndex(null);
        } else {
          // Existing field - use the auto-save
          await saveFieldImmediately(field.id, field, false);
        }
      } catch (error) {
        logger.error('Error saving field:', error);
        toast.error(field.id.startsWith('temp-') ? 'Failed to create field' : 'Failed to save field');
      } finally {
        // Remove field from saving state
        setSavingFieldIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(field.id);
          return newSet;
        });
      }
    },
    [formData.fields, saveFieldImmediately, moduleId, organizationId, formId, dispatch],
  );

  const isFieldPendingSave = useCallback(
    (index: number) => {
      const field = formData.fields[index];
      if (field) {
        // Check if field is currently being saved manually
        if (savingFieldIds.has(field.id)) {
          return true;
        }
        // Check if field has pending auto-save
        return hasPendingSave(field.id);
      }
      return false;
    },
    [formData.fields, savingFieldIds, hasPendingSave],
  );

  const isRootCanvasFieldConfigured = useCallback(
    (index: number) => {
      const field = formData.fields[index];
      if (!field || field.type !== FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS) {
        return true; // Not a root canvas field, so it's "configured"
      }

      const canvasId = field.properties?.canvasId;
      const canvasCreated = field.properties?.canvasCreated;
      const isRootField = field.properties?.isRootField;
      const referencedFieldId = field.properties?.referencedFieldId;

      // Must have canvas ID and be created
      if (!canvasId || !canvasCreated) {
        return false;
      }

      // Must be either a root field OR have a referenced field
      if (!isRootField && !referencedFieldId) {
        return false;
      }

      return true;
    },
    [formData.fields],
  );

  const handleSaveFormName = useCallback(async () => {
    if (!moduleId || !organizationId || !formId || formId === 'new') {
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Form name is required');
      return;
    }

    if (formData.name.length > 100) {
      toast.error('Form name must be less than 100 characters');
      return;
    }

    setIsSavingFormName(true);

    try {
      // Use PATCH for partial updates (just name/description)
      const result = await processModuleApi.patchForm(organizationId, moduleId, formId, {
        name: formData.name,
      });

      if (!result.ok) {
        logger.error('Failed to update form name', result);
        toast.error('Failed to save form name');
        return;
      }

      // Update the original form name to reflect the saved state
      setOriginalFormName(formData.name);
      toast.success('Form name saved successfully');

      // Refresh forms data
      if (moduleId) {
        dispatch(thunks.getFormsForProcessModule(moduleId));
      }
    } catch (error) {
      logger.error('Error saving form name:', error);
      toast.error('Failed to save form name');
    } finally {
      setIsSavingFormName(false);
    }
  }, [formData.name, moduleId, organizationId, formId, dispatch]);

  // Check if form name has changes
  const hasFormNameChanges = formData.name !== originalFormName;

  const handleSubmit = useCallback(async () => {
    if (!moduleId) {
      toast.error('Process module ID is required');
      return;
    }

    if (!organizationId) {
      toast.error('Organization information not available');
      return;
    }

    // Flush all pending field saves before submitting form
    await flushAllPendingSaves();

    const validateForm = (): string[] => {
      const errors: string[] = [];

      if (!formData.name.trim()) {
        errors.push('Form name is required');
      }
      if (formData.name.length > 100) {
        errors.push('Form name must be less than 100 characters');
      }

      // Basic field validation - ensure all fields have been saved
      formData.fields.forEach((field, index) => {
        if (field.id.startsWith('temp-')) {
          errors.push(`Field ${index + 1} must be saved before creating the form`);
        }
      });

      return errors;
    };

    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform form data for creation with full field objects
      const transformedFormData: FormInput = {
        name: formData.name,
        description: '', // Description field removed from UI
        fields: formData.fields,
      };

      logger.log('creating form', transformedFormData);
      const result = await processModuleApi.createForm(organizationId, moduleId, transformedFormData);

      if (!result.ok) {
        logger.error('Failed to create form', result);
        toast.error('Failed to create form');
        return;
      }

      toast.success('Form created successfully');
      if (!result.result?.form) {
        logger.error('Form creation response missing form data', result);
        toast.error('Form created but response is missing form data');
        return;
      }
      const newFormId = result.result.form.id;
      navigate(paths.settings.formBuilder.formEdit(organizationId, moduleId, newFormId));
    } catch (error) {
      logger.error('Error creating form:', error);
      toast.error('Failed to create form');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, moduleId, organizationId, navigate, flushAllPendingSaves]);

  const handleDragEnd = useCallback(
    async (result: any) => {
      if (!result.destination) return;

      // Reorder field data
      const items = Array.from(formData.fields);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      setFormData({
        ...formData,
        fields: items,
      });

      // Update form with new field order
      if (organizationId && moduleId && formId && formId !== 'new') {
        try {
          const updatedFormData = {
            name: formData.name,
            description: '', // Description field removed from UI
            fields: items, // Send complete field objects, backend needs full field data
            sequence: form?.sequence, // Preserve form sequence when reordering fields
          };
          await processModuleApi.updateForm(organizationId, moduleId, formId, updatedFormData);
          toast.success('Fields reordered');
        } catch (error) {
          toast.error('Failed to reorder fields');
        }
      }
    },
    [formData, organizationId, moduleId, formId, form],
  );

  if (isEditMode && !formId) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h6" color="error">
            Loading form...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!organizationId) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h6" color="error">
            Loading process module information...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link color="inherit" href={paths.settings.formBuilder.moduleList(organizationId || '')}>
          Process Modules
        </Link>
        <Link color="inherit" href={paths.settings.formBuilder.moduleForms(organizationId || '', moduleId || '')}>
          {currentModule?.name || 'Module'}
        </Link>
        <Typography color="text.primary">{isEditMode ? formData.name || 'Form' : 'New Form'}</Typography>
      </Breadcrumbs>
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Form Builder' : 'Create New Form'}
        </Typography>

        <Grid container spacing={3}>
          {/* Form Configuration Panel */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Form Name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                    }}
                    required
                    disabled={isSubmitting || isSavingFormName}
                    error={formData.name.length > 100}
                    helperText={formData.name.length > 100 ? 'Name must be less than 100 characters' : ''}
                  />
                  {isEditMode && hasFormNameChanges && (
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setFormData({ ...formData, name: originalFormName });
                        }}
                        disabled={isSavingFormName}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveFormName}
                        disabled={isSavingFormName || !formData.name.trim() || formData.name.length > 100}
                        sx={{ textTransform: 'none' }}
                      >
                        {isSavingFormName ? 'Saving...' : 'Save'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Paper>

              <Typography variant="h6" gutterBottom>
                Fields
              </Typography>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="fields">
                  {(provided) => (
                    <div {...provided.droppableProps} key="fields" ref={provided.innerRef}>
                      {formData.fields.map((field: FieldInput & Partial<Field>, index: number) => {
                        return (
                          <FieldEditor
                            key={field.id}
                            moduleId={moduleId || ''}
                            field={field}
                            index={index}
                            handleFieldChange={handleFieldChange}
                            handleRemoveField={handleRemoveField}
                            handleFieldSelection={handleFieldSelection}
                            handleSaveField={handleSaveField}
                            isSubmitting={isSubmitting}
                            isFieldUsingExisting={isFieldUsingExisting}
                            isFieldPendingSave={isFieldPendingSave}
                            isRootCanvasFieldConfigured={isRootCanvasFieldConfigured}
                            allFormFields={formData.fields}
                            expanded={expandedFieldIndex === index}
                            onAccordionChange={handleAccordionChange}
                          />
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {isEditMode && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                  disabled={isSubmitting}
                  variant="outlined"
                  fullWidth
                  data-testid={TEST_IDS.FORM_BUILDER.ADD_FIELD_BUTTON}
                >
                  Add Field
                </Button>
              )}

              {!isEditMode && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    disabled={isSubmitting}
                    fullWidth
                  >
                    {isSubmitting ? 'Creating...' : 'Create Form'}
                  </Button>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Preview Panel */}
          <Grid item xs={12} md={5}>
            <Paper
              sx={{
                p: 3,
                position: 'sticky',
                top: 24,
                bgcolor: 'background.default',
              }}
            >
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PreviewIcon color="primary" />
                  <Typography variant="h6">Live Preview</Typography>
                </Stack>

                <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                  <Typography variant="h5" gutterBottom>
                    {formData.name || 'Untitled Form'}
                  </Typography>
                  <Stack spacing={3}>
                    {formData.fields.map((field: FieldInput & Partial<Field>, index: number) => {
                      return (
                        <Box key={field.id || index}>
                          <FormFieldPreview field={field} />
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default FormBuilder;
